import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Property } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { CameroonFlag } from './CameroonFlag';
import { CAMEROON_REGIONS, ALL_CAMEROON_TOWNS, CAMEROON_CITIES_BY_REGION } from '../data/cameroonCities';
import { slugify, getRegionBySlug } from '../lib/routes';
import { MapPin, Compass, Building, ArrowRight, Layers, Globe, ChevronDown, ChevronUp } from 'lucide-react';

interface CameroonRegionalDirectoryProps {
  properties?: Property[];
  className?: string;
}

interface RegionDirectoryItem {
  id: string;
  name: string;
  nameFr: string;
  capital: string;
  slug: string;
  totalTowns: number;
  keyCities: { name: string; slug: string; note?: string; noteFr?: string }[];
}

export const CameroonRegionalDirectory: React.FC<CameroonRegionalDirectoryProps> = ({
  properties = [],
  className = '',
}) => {
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const [activeTab, setActiveTab] = useState<'regions' | 'cities'>('regions');
  const [isExpanded, setIsExpanded] = useState(false);

  // Compute live listing counts per region and category
  const countsByRegion = useMemo(() => {
    const map: Record<string, { total: number; land: number; residential: number; commercial: number; rent: number }> = {};

    CAMEROON_REGIONS.forEach((r) => {
      map[r.name.toLowerCase()] = { total: 0, land: 0, residential: 0, commercial: 0, rent: 0 };
    });

    properties.forEach((p) => {
      const loc = (p.location || '').toLowerCase();
      const pRegion = (p.region || '').toLowerCase();

      for (const r of CAMEROON_REGIONS) {
        const rName = r.name.toLowerCase();
        const cities = (CAMEROON_CITIES_BY_REGION[r.name] || []).map((c) => c.toLowerCase());
        const matches = pRegion === rName || loc.includes(rName) || cities.some((c) => loc.includes(c));

        if (matches) {
          const entry = map[rName];
          if (entry) {
            entry.total += 1;
            if (p.type === 'land') entry.land += 1;
            else if (p.type === 'house' || p.type === 'apartment' || p.type === 'studio') entry.residential += 1;
            else if (p.type === 'commercial') entry.commercial += 1;

            if (p.categoryBadge?.includes('RENT') || p.transactionType === 'rent') {
              entry.rent += 1;
            }
          }
          break;
        }
      }
    });

    return map;
  }, [properties]);

  // Compute live listing counts per flagship city
  const countsByCity = useMemo(() => {
    const map: Record<string, { total: number; land: number; residential: number; commercial: number; rent: number }> = {};

    properties.forEach((p) => {
      const loc = (p.location || '').toLowerCase();
      const cName = (p.city || '').toLowerCase();

      ALL_CAMEROON_TOWNS.forEach((town) => {
        const tLower = town.name.toLowerCase();
        if (cName === tLower || loc.includes(tLower)) {
          if (!map[town.slug]) {
            map[town.slug] = { total: 0, land: 0, residential: 0, commercial: 0, rent: 0 };
          }
          const entry = map[town.slug];
          entry.total += 1;
          if (p.type === 'land') entry.land += 1;
          else if (p.type === 'house' || p.type === 'apartment' || p.type === 'studio') entry.residential += 1;
          else if (p.type === 'commercial') entry.commercial += 1;

          if (p.categoryBadge?.includes('RENT') || p.transactionType === 'rent') {
            entry.rent += 1;
          }
        }
      });
    });

    return map;
  }, [properties]);

  // 10 Official Regions in Economic Hub Priority Order with top authentic towns
  const regionData: RegionDirectoryItem[] = useMemo(
    () => [
      {
        id: 'centre',
        name: 'Centre',
        nameFr: 'Centre',
        capital: 'Yaoundé',
        slug: 'centre',
        totalTowns: 19,
        keyCities: [
          { name: 'Yaoundé', slug: 'yaounde', note: 'Bastos, Mendong, Odza', noteFr: 'Bastos, Mendong, Odza' },
          { name: 'Mbalmayo', slug: 'mbalmayo', note: 'Nyong Corridor', noteFr: 'Axe du Nyong' },
          { name: 'Soa', slug: 'soa', note: 'University Campus', noteFr: 'Cité Universitaire' },
          { name: 'Obala', slug: 'obala', note: 'Agricultural Hub', noteFr: 'Bassin Agricole' },
          { name: 'Mfou', slug: 'mfou', note: 'South Suburban', noteFr: 'Extension Sud' },
        ],
      },
      {
        id: 'littoral',
        name: 'Littoral',
        nameFr: 'Littoral',
        capital: 'Douala',
        slug: 'littoral',
        totalTowns: 13,
        keyCities: [
          { name: 'Douala', slug: 'douala', note: 'Bonanjo, Akwa, Bonamoussadi', noteFr: 'Bonanjo, Akwa, Bonamoussadi' },
          { name: 'Edéa', slug: 'edea', note: 'Industrial & Hydro Power', noteFr: 'Pôle Industriel & Hydroélectrique' },
          { name: 'Nkongsamba', slug: 'nkongsamba', note: 'Agro-commerce', noteFr: 'Agro-commerce & Hauts Plateaux' },
          { name: 'Loum', slug: 'loum', note: 'Agro-industrial Corridor', noteFr: 'Bassin Agro-industriel' },
          { name: 'Penja', slug: 'penja', note: 'World Famous Pepper Soils', noteFr: 'Terres Fertiles du Poivre IGP' },
        ],
      },
      {
        id: 'south-west',
        name: 'South-West',
        nameFr: 'Sud-Ouest',
        capital: 'Buea',
        slug: 'south-west',
        totalTowns: 14,
        keyCities: [
          { name: 'Buea', slug: 'buea', note: 'Mount Cameroon, Silicon Mountain', noteFr: 'Pied du Mont Cameroun' },
          { name: 'Limbe', slug: 'limbe', note: 'Atlantic Coast & Botanical Haven', noteFr: 'Cité Balnéaire & Portuaire' },
          { name: 'Kumba', slug: 'kumba', note: 'Commercial Crossroads', noteFr: 'Carrefour Commercial' },
          { name: 'Tiko', slug: 'tiko', note: 'Coastal Plains', noteFr: 'Plaines Côtières' },
          { name: 'Mutengene', slug: 'mutengene', note: 'Strategic Junction', noteFr: 'Carrefour Stratégique' },
        ],
      },
      {
        id: 'south',
        name: 'South',
        nameFr: 'Sud',
        capital: 'Ebolowa',
        slug: 'south',
        totalTowns: 13,
        keyCities: [
          { name: 'Kribi', slug: 'kribi', note: 'Deep Sea Port & White Sand Beaches', noteFr: 'Port en Eau Profonde & Plages' },
          { name: 'Ebolowa', slug: 'ebolowa', note: 'Regional Capital & Cocoa Belt', noteFr: 'Capitale Régionale' },
          { name: 'Campo', slug: 'campo', note: 'Atlantic Border & National Park', noteFr: 'Frontière Maritime & Parc National' },
          { name: 'Sangmélima', slug: 'sangmelima', note: 'Cross-border Crossroads', noteFr: 'Carrefour Équatorial' },
          { name: 'Ambam', slug: 'ambam', note: 'Gabon & Eq. Guinea Border Gateway', noteFr: 'Frontière Gabon & Guinée Éq.' },
        ],
      },
      {
        id: 'west',
        name: 'West',
        nameFr: 'Ouest',
        capital: 'Bafoussam',
        slug: 'west',
        totalTowns: 18,
        keyCities: [
          { name: 'Bafoussam', slug: 'bafoussam', note: 'Highlands Commercial Capital', noteFr: 'Capitale Commerciale de l’Ouest' },
          { name: 'Dschang', slug: 'dschang', note: 'University & Climate Resort', noteFr: 'Ville Universitaire & Climat Tempéré' },
          { name: 'Foumban', slug: 'foumban', note: 'Historic Sultanate & Arts', noteFr: 'Cité des Arts & Sultanat Bamoun' },
          { name: 'Bangangté', slug: 'bangangte', note: 'Ndé Division & Medical Academy', noteFr: 'Chef-lieu du Ndé & Pôle Santé' },
          { name: 'Bafang', slug: 'bafang', note: 'Haut-Nkam Agro-Commerce', noteFr: 'Haut-Nkam Agro-Industriel' },
        ],
      },
      {
        id: 'north-west',
        name: 'North-West',
        nameFr: 'Nord-Ouest',
        capital: 'Bamenda',
        slug: 'north-west',
        totalTowns: 13,
        keyCities: [
          { name: 'Bamenda', slug: 'bamenda', note: 'Highlands Metropolis', noteFr: 'Métropole des Hauts Plateaux' },
          { name: 'Kumbo', slug: 'kumbo', note: 'Bui Highlands & Fon Palace', noteFr: 'Hauts Plateaux du Bui' },
          { name: 'Ndop', slug: 'ndop', note: 'Fertile Rice Plains', noteFr: 'Plaines Rizicoles' },
          { name: 'Mbengwi', slug: 'mbengwi', note: 'Momo Division Capital', noteFr: 'Chef-lieu de la Momo' },
          { name: 'Wum', slug: 'wum', note: 'Menchum Scenic Crater Lakes', noteFr: 'Lacs de Cratère du Menchum' },
        ],
      },
      {
        id: 'adamawa',
        name: 'Adamawa',
        nameFr: 'Adamaoua',
        capital: 'Ngaoundéré',
        slug: 'adamawa',
        totalTowns: 8,
        keyCities: [
          { name: 'Ngaoundéré', slug: 'ngaoundere', note: 'Railway Terminal & Plateau Capital', noteFr: 'Terminus Ferroviaire & Pôle Agro' },
          { name: 'Meiganga', slug: 'meiganga', note: 'Cross-corridor Mining & Cattle', noteFr: 'Carrefour Minier & Pastoral' },
          { name: 'Tibati', slug: 'tibati', note: 'Djérem Lake & Fisheries', noteFr: 'Lac de Tibati & Pêche' },
          { name: 'Banyo', slug: 'banyo', note: 'Historic Sultanate & Highlands', noteFr: 'Sultanat & Pâturages' },
        ],
      },
      {
        id: 'north',
        name: 'North',
        nameFr: 'Nord',
        capital: 'Garoua',
        slug: 'north',
        totalTowns: 11,
        keyCities: [
          { name: 'Garoua', slug: 'garoua', note: 'Bénoué River Port & Cotton Capital', noteFr: 'Port Fluvial de la Bénoué' },
          { name: 'Guider', slug: 'guider', note: 'Mayo-Louti Cultural Crossroads', noteFr: 'Gorges de Kola & Carrefour' },
          { name: 'Figuil', slug: 'figuil', note: 'Cement & Industrial Mineral Hub', noteFr: 'Cimenterie & Bassin Minier' },
          { name: 'Rey-Bouba', slug: 'rey-bouba', note: 'Historic Lamidat & Savannahs', noteFr: 'Lamidat Historique du Mayo-Rey' },
        ],
      },
      {
        id: 'far-north',
        name: 'Far North',
        nameFr: 'Extrême-Nord',
        capital: 'Maroua',
        slug: 'far-north',
        totalTowns: 14,
        keyCities: [
          { name: 'Maroua', slug: 'maroua', note: 'Diamaré Capital & Sahel Crossroads', noteFr: 'Capitale du Diamaré' },
          { name: 'Kousséri', slug: 'kousseri', note: 'Chari River Border (facing N’Djamena)', noteFr: 'Frontière Tchad (face à N’Djamena)' },
          { name: 'Mokolo', slug: 'mokolo', note: 'Mandara Mountains Scenic Terraces', noteFr: 'Monts Mandara' },
          { name: 'Yagoua', slug: 'yagoua', note: 'Danay River Rice Basin', noteFr: 'Bassin Rizicole du Mayo-Danay' },
        ],
      },
      {
        id: 'east',
        name: 'East',
        nameFr: 'Est',
        capital: 'Bertoua',
        slug: 'east',
        totalTowns: 12,
        keyCities: [
          { name: 'Bertoua', slug: 'bertoua', note: 'Trans-African Mining & Forestry Capital', noteFr: 'Capitale Forestière & Minière' },
          { name: 'Batouri', slug: 'batouri', note: 'Kadey Gold Mining Basin', noteFr: 'Bassin Aurifère de la Kadey' },
          { name: 'Abong-Mbang', slug: 'abong-mbang', note: 'Upper Nyong Commercial Hub', noteFr: 'Carrefour du Haut-Nyong' },
          { name: 'Yokadouma', slug: 'yokadouma', note: 'Boumba Rainforest Timber Reserve', noteFr: 'Réserves de la Boumba' },
        ],
      },
    ],
    []
  );

  // 12 Flagship Major City Hubs in Economic Priority Order
  const flagshipCities = useMemo(
    () => [
      { name: 'Yaoundé', slug: 'yaounde', region: 'Centre', roleEn: 'Diplomatic & Political Capital', roleFr: 'Capitale Politique & Diplomatique' },
      { name: 'Douala', slug: 'douala', region: 'Littoral', roleEn: 'Maritime & Economic Powerhouse', roleFr: 'Poumon Économique & Portuaire' },
      { name: 'Buea', slug: 'buea', region: 'South-West', roleEn: 'Mount Cameroon Tech & University Hub', roleFr: 'Pied du Mont Cameroun & Pôle Universitaire' },
      { name: 'Limbe', slug: 'limbe', region: 'South-West', roleEn: 'Atlantic Beachfront & Oil Hub', roleFr: 'Cité Balnéaire & Raffinerie' },
      { name: 'Kribi', slug: 'kribi', region: 'South', roleEn: 'Deep Sea Port & Prime Beachfront', roleFr: 'Port en Eau Profonde & Plages Blanches' },
      { name: 'Bafoussam', slug: 'bafoussam', region: 'West', roleEn: 'Highlands Agro-Commercial Capital', roleFr: 'Capitale Commerciale des Hauts Plateaux' },
      { name: 'Ebolowa', slug: 'ebolowa', region: 'South', roleEn: 'Southern Forestry & Border Gateway', roleFr: 'Chef-lieu du Sud & Corridor Frontalier' },
      { name: 'Bamenda', slug: 'bamenda', region: 'North-West', roleEn: 'North-West Regional Metropolis', roleFr: 'Métropole Régionale du Nord-Ouest' },
      { name: 'Ngaoundéré', slug: 'ngaoundere', region: 'Adamawa', roleEn: 'Trans-Cameroon Rail Terminal', roleFr: 'Terminus du Transcam & Plateau' },
      { name: 'Garoua', slug: 'garoua', region: 'North', roleEn: 'Bénoué River Port & Cotton Capital', roleFr: 'Port Fluvial & Capitale du Coton' },
      { name: 'Maroua', slug: 'maroua', region: 'Far North', roleEn: 'Sahel Trade & Tourism Crossroads', roleFr: 'Carrefour Sahélien & Tourisme' },
      { name: 'Bertoua', slug: 'bertoua', region: 'East', roleEn: 'Eastern Gold & Timber Gateway', roleFr: 'Porte Minière & Forestière de l’Est' },
    ],
    []
  );

  return (
    <section className={`relative w-full bg-[#080d14] text-white border-t border-slate-800/80 overflow-hidden ${className}`}>

      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-5 z-10">
        {/* Compact Summary Bar & Toggle Trigger */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 w-full md:w-auto">
            <CameroonFlag className="w-6 h-4 shadow-xs shrink-0" />
            <div>
              <h3 className="text-sm sm:text-base font-extrabold text-white flex items-center gap-2">
                <span>{isFr ? 'Annuaire Cadastral des 10 Régions du Cameroun' : 'Cameroon Cadastral Directory: 10 Regions & Hubs'}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800/90 text-slate-300 font-bold border border-slate-700/80">
                  {isFr ? '80+ Villes & Cadastre' : '80+ Towns & Cadastre'}
                </span>
              </h3>
              <p className="text-xs text-slate-400 hidden sm:block">
                {isFr
                  ? 'Titres Fonciers, bornage cadastral et annonces immobilières par chef-lieu et région.'
                  : 'Land titles, cadastral boundaries, and property listings across every regional hub.'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap justify-between md:justify-end w-full md:w-auto">
            {/* Quick Hub Badges */}
            <div className="hidden lg:flex items-center gap-1.5 text-xs text-slate-300">
              <Link to={isFr ? '/fr/terrains-a-vendre/centre/yaounde' : '/en/land-for-sale/centre/yaounde'} className="px-2.5 py-1 rounded-lg bg-slate-900/90 hover:bg-slate-800 border border-slate-800 hover:text-white transition-colors">
                Yaoundé
              </Link>
              <Link to={isFr ? '/fr/terrains-a-vendre/littoral/douala' : '/en/land-for-sale/littoral/douala'} className="px-2.5 py-1 rounded-lg bg-slate-900/90 hover:bg-slate-800 border border-slate-800 hover:text-white transition-colors">
                Douala
              </Link>
              <Link to={isFr ? '/fr/terrains-a-vendre/sud-ouest/buea' : '/en/land-for-sale/south-west/buea'} className="px-2.5 py-1 rounded-lg bg-slate-900/90 hover:bg-slate-800 border border-slate-800 hover:text-white transition-colors">
                Buea
              </Link>
              <Link to={isFr ? '/fr/terrains-a-vendre/sud-ouest/limbe' : '/en/land-for-sale/south-west/limbe'} className="px-2.5 py-1 rounded-lg bg-slate-900/90 hover:bg-slate-800 border border-slate-800 hover:text-white transition-colors">
                Limbe
              </Link>
              <Link to={isFr ? '/fr/terrains-a-vendre/sud/kribi' : '/en/land-for-sale/south/kribi'} className="px-2.5 py-1 rounded-lg bg-slate-900/90 hover:bg-slate-800 border border-slate-800 hover:text-white transition-colors">
                Kribi
              </Link>
              <Link to={isFr ? '/fr/terrains-a-vendre/ouest/bafoussam' : '/en/land-for-sale/west/bafoussam'} className="px-2.5 py-1 rounded-lg bg-slate-900/90 hover:bg-slate-800 border border-slate-800 hover:text-white transition-colors">
                Bafoussam
              </Link>
            </div>

            {/* Toggle Button */}
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-800/90 hover:bg-slate-700 border border-slate-700/80 text-xs font-bold text-white transition-all cursor-pointer shadow-sm hover:scale-102"
              aria-expanded={isExpanded}
            >
              <span>{isExpanded ? (isFr ? 'Masquer l’annuaire complet' : 'Collapse directory') : (isFr ? 'Explorer les 10 régions & 80+ villes' : 'Explore all 10 regions & 80+ cities')}</span>
              {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-amber-400" /> : <ChevronDown className="w-3.5 h-3.5 text-amber-400" />}
            </button>
          </div>
        </div>

        {/* Expandable Full Directory Content */}
        {isExpanded && (
          <div className="pt-6 border-t border-slate-800/80 mt-4 animate-fadeIn">
            {/* Directory Header with Tab Toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 pb-4 border-b border-slate-800/80">
              <p className="text-xs sm:text-sm text-slate-300 max-w-2xl leading-relaxed">
                {isFr
                  ? 'Accédez directement aux parcelles avec Titre Foncier, villas modernes, locaux commerciaux et locations dans chaque région et ville administrative du Cameroun.'
                  : 'Direct access to titled land parcels (Titre Foncier), modern villas, commercial spaces, and rentals across every administrative region and city in Cameroon.'}
              </p>

              {/* Directory Tab Toggle */}
              <div className="flex items-center gap-1.5 p-1 rounded-xl bg-slate-950 border border-slate-800 shrink-0">
                <button
                  type="button"
                  onClick={() => setActiveTab('regions')}
                  className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    activeTab === 'regions'
                      ? 'bg-slate-800 text-amber-400 border border-slate-700/80 shadow-xs'
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Layers className="w-3.5 h-3.5" />
                  <span>{isFr ? 'Les 10 Régions' : 'All 10 Regions'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('cities')}
                  className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    activeTab === 'cities'
                      ? 'bg-slate-800 text-amber-400 border border-slate-700/80 shadow-xs'
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Building className="w-3.5 h-3.5" />
                  <span>{isFr ? 'Villes Économiques' : 'Economic Hubs'}</span>
                </button>
              </div>
            </div>

        {/* ============================================================ */}
        {/* VIEW 1: ALL 10 REGIONS (3-COLUMN RESPONSIVE DIRECTORY GRID)  */}
        {/* ============================================================ */}
        {activeTab === 'regions' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 lg:gap-x-12 gap-y-10 sm:gap-y-12">
            {regionData.map((reg) => {
              const regKey = reg.name.toLowerCase();
              const counts = countsByRegion[regKey] || { total: 0, land: 0, residential: 0, commercial: 0, rent: 0 };
              const regConfig = getRegionBySlug(reg.slug);
              const regSlugEn = regConfig?.slug || reg.slug;
              const regSlugFr = regConfig?.slugFr || reg.slug;
              const regionTitle = isFr ? `Région du ${reg.nameFr}` : `${reg.name} Region`;
              const regionUrl = isFr ? `/fr/regions/${regSlugFr}` : `/en/regions/${regSlugEn}`;

              return (
                <div key={reg.id} className="space-y-3">
                  {/* Entity Header: Flag + Region Name (Clickable Unique SEO URL) */}
                  <div className="flex items-center gap-2.5">
                    <CameroonFlag className="w-5 h-3.5 shadow-2xs" />
                    <Link
                      to={regionUrl}
                      className="text-base sm:text-lg font-extrabold text-white hover:text-amber-300 transition-colors tracking-tight inline-flex items-center gap-1.5 group"
                      title={isFr ? `Guide immobilier de la Région du ${reg.nameFr}` : `${reg.name} Region property guide`}
                    >
                      <span>{regionTitle}</span>
                      <span className="text-[11px] font-semibold text-slate-400 bg-slate-900/90 border border-slate-800 px-2 py-0.5 rounded-full ml-1">
                        {reg.capital}
                      </span>
                    </Link>
                  </div>

                  {/* Bulleted List of Category and City SEO Links */}
                  <ul className="space-y-1.5 pl-1.5 text-xs sm:text-[13px] text-slate-300">
                    {/* 1. Land for Sale */}
                    <li>
                      <Link
                        to={isFr ? `/fr/terrains-a-vendre/${regSlugFr}` : `/en/land-for-sale/${regSlugEn}`}
                        className="inline-flex items-center hover:text-white transition-colors group"
                      >
                        <span className="text-amber-400/80 mr-2 font-bold text-sm leading-none">•</span>
                        <span className="group-hover:underline">
                          {isFr ? 'Terrains & Parcelles à Vendre' : 'Land & Plots for Sale'}
                        </span>
                        {counts.land > 0 && (
                          <span className="ml-1.5 text-[11px] text-slate-400 font-semibold opacity-90">
                            ({counts.land})
                          </span>
                        )}
                      </Link>
                    </li>

                    {/* 2. Residential & Houses */}
                    <li>
                      <Link
                        to={isFr ? `/fr/maisons-a-vendre/${regSlugFr}` : `/en/houses-for-sale/${regSlugEn}`}
                        className="inline-flex items-center hover:text-white transition-colors group"
                      >
                        <span className="text-amber-400/80 mr-2 font-bold text-sm leading-none">•</span>
                        <span className="group-hover:underline">
                          {isFr ? 'Maisons, Villas & Duplex' : 'Residential, Houses & Villas'}
                        </span>
                        {counts.residential > 0 && (
                          <span className="ml-1.5 text-[11px] text-slate-400 font-semibold opacity-90">
                            ({counts.residential})
                          </span>
                        )}
                      </Link>
                    </li>

                    {/* 3. Commercial Real Estate */}
                    <li>
                      <Link
                        to={isFr ? `/fr/immobilier-commercial-a-vendre/${regSlugFr}` : `/en/commercial-for-sale/${regSlugEn}`}
                        className="inline-flex items-center hover:text-white transition-colors group"
                      >
                        <span className="text-amber-400/80 mr-2 font-bold text-sm leading-none">•</span>
                        <span className="group-hover:underline">
                          {isFr ? 'Immobilier Commercial & Locaux' : 'Commercial Real Estate & Shops'}
                        </span>
                        {counts.commercial > 0 && (
                          <span className="ml-1.5 text-[11px] text-slate-400 font-semibold opacity-90">
                            ({counts.commercial})
                          </span>
                        )}
                      </Link>
                    </li>

                    {/* 4. Long-Term Rentals */}
                    <li>
                      <Link
                        to={isFr ? `/fr/biens-a-louer/${regSlugFr}` : `/en/properties-for-rent/${regSlugEn}`}
                        className="inline-flex items-center hover:text-white transition-colors group"
                      >
                        <span className="text-amber-400/80 mr-2 font-bold text-sm leading-none">•</span>
                        <span className="group-hover:underline">
                          {isFr ? 'Locations Longue Durée' : 'Long-Term Rentals'}
                        </span>
                        {counts.rent > 0 && (
                          <span className="ml-1.5 text-[11px] text-slate-400 font-semibold opacity-90">
                            ({counts.rent})
                          </span>
                        )}
                      </Link>
                    </li>

                    {/* 5. Short Stays */}
                    <li>
                      <Link
                        to={isFr ? `/fr/courts-sejours/${regSlugFr}` : `/en/short-stays/${regSlugEn}`}
                        className="inline-flex items-center hover:text-white transition-colors group"
                      >
                        <span className="text-amber-400/80 mr-2 font-bold text-sm leading-none">•</span>
                        <span className="group-hover:underline">
                          {isFr ? 'Courts Séjours & Meublés' : 'Short-Term Stays & Furnished'}
                        </span>
                      </Link>
                    </li>

                    {/* Key Towns in this region (Clean 3-level URLs) */}
                    {reg.keyCities.slice(0, 4).map((city) => (
                      <li key={city.slug}>
                        <Link
                          to={isFr ? `/fr/terrains-a-vendre/${regSlugFr}/${city.slug}` : `/en/land-for-sale/${regSlugEn}/${city.slug}`}
                          className="inline-flex items-center hover:text-white transition-colors group"
                          title={isFr ? `Immobilier et terrains à ${city.name}` : `Real estate and land in ${city.name}`}
                        >
                          <span className="text-amber-400/90 mr-2 font-bold text-sm leading-none">•</span>
                          <span className="font-medium group-hover:underline text-slate-200">
                            {city.name}
                          </span>
                          {(city.note || city.noteFr) && (
                            <span className="ml-1.5 text-[11px] text-slate-400/80">
                              ({isFr ? city.noteFr || city.note : city.note})
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}

                    {/* All Towns in Region Link */}
                    <li className="pt-1">
                      <Link
                        to={regionUrl}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-400 hover:text-amber-300 hover:underline transition-colors"
                      >
                        <span>
                          {isFr ? `Voir les ${reg.totalTowns} villes du ${reg.nameFr} →` : `Explore all ${reg.totalTowns} towns in ${reg.name} →`}
                        </span>
                      </Link>
                    </li>
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {/* ============================================================ */}
        {/* VIEW 2: 12 MAJOR ECONOMIC HUBS (3-COLUMN RESPONSIVE GRID)    */}
        {/* ============================================================ */}
        {activeTab === 'cities' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 lg:gap-x-12 gap-y-10 sm:gap-y-12">
            {flagshipCities.map((city) => {
              const cityCounts = countsByCity[city.slug] || { total: 0, land: 0, residential: 0, commercial: 0, rent: 0 };
              const cityGuideUrl = isFr ? `/fr/villes/${city.slug}` : `/en/cities/${city.slug}`;

              return (
                <div key={city.slug} className="space-y-3">
                  {/* Entity Header: Flag + City Name (Clickable Unique SEO URL) */}
                  <div className="flex items-center gap-2.5">
                    <CameroonFlag className="w-5 h-3.5 shadow-2xs" />
                    <Link
                      to={cityGuideUrl}
                      className="text-base sm:text-lg font-extrabold text-white hover:text-amber-300 transition-colors tracking-tight inline-flex items-center gap-1.5 group"
                      title={isFr ? `Guide immobilier ${city.name}` : `${city.name} property guide`}
                    >
                      <span>{city.name}</span>
                      <span className="text-[11px] font-semibold text-slate-400 bg-slate-900/90 border border-slate-800 px-2 py-0.5 rounded-full ml-1">
                        {city.region}
                      </span>
                    </Link>
                  </div>

                  <p className="text-[11px] text-slate-400 font-medium pl-1.5 leading-snug">
                    {isFr ? city.roleFr : city.roleEn}
                  </p>

                  {/* Bulleted List of City Specific SEO Category Links */}
                  <ul className="space-y-1.5 pl-1.5 text-xs sm:text-[13px] text-slate-300">
                    <li>
                      <Link
                        to={isFr ? `/fr/terrains-a-vendre/${city.slug}` : `/en/land-for-sale/${city.slug}`}
                        className="inline-flex items-center hover:text-white transition-colors group"
                      >
                        <span className="text-amber-400/80 mr-2 font-bold text-sm leading-none">•</span>
                        <span className="group-hover:underline">
                          {isFr ? `Terrains Titrés à ${city.name}` : `Titled Land in ${city.name}`}
                        </span>
                        {cityCounts.land > 0 && (
                          <span className="ml-1.5 text-[11px] text-slate-400 font-semibold opacity-90">
                            ({cityCounts.land})
                          </span>
                        )}
                      </Link>
                    </li>

                    <li>
                      <Link
                        to={isFr ? `/fr/maisons-a-vendre/${city.slug}` : `/en/houses-for-sale/${city.slug}`}
                        className="inline-flex items-center hover:text-white transition-colors group"
                      >
                        <span className="text-amber-400/80 mr-2 font-bold text-sm leading-none">•</span>
                        <span className="group-hover:underline">
                          {isFr ? `Maisons & Villas à ${city.name}` : `Houses & Villas in ${city.name}`}
                        </span>
                        {cityCounts.residential > 0 && (
                          <span className="ml-1.5 text-[11px] text-slate-400 font-semibold opacity-90">
                            ({cityCounts.residential})
                          </span>
                        )}
                      </Link>
                    </li>

                    <li>
                      <Link
                        to={isFr ? `/fr/biens-a-louer/${city.slug}` : `/en/properties-for-rent/${city.slug}`}
                        className="inline-flex items-center hover:text-white transition-colors group"
                      >
                        <span className="text-amber-400/80 mr-2 font-bold text-sm leading-none">•</span>
                        <span className="group-hover:underline">
                          {isFr ? `Appartements en Location à ${city.name}` : `Rental Apartments in ${city.name}`}
                        </span>
                        {cityCounts.rent > 0 && (
                          <span className="ml-1.5 text-[11px] text-slate-400 font-semibold opacity-90">
                            ({cityCounts.rent})
                          </span>
                        )}
                      </Link>
                    </li>

                    <li>
                      <Link
                        to={isFr ? `/fr/courts-sejours/${city.slug}` : `/en/short-stays/${city.slug}`}
                        className="inline-flex items-center hover:text-white transition-colors group"
                      >
                        <span className="text-amber-400/80 mr-2 font-bold text-sm leading-none">•</span>
                        <span className="group-hover:underline">
                          {isFr ? `Courts Séjours & Meublés à ${city.name}` : `Short Stays & Furnished in ${city.name}`}
                        </span>
                      </Link>
                    </li>

                    <li>
                      <Link
                        to={isFr ? `/fr/verifier-propriete?city=${city.slug}` : `/en/verify-property?city=${city.slug}`}
                        className="inline-flex items-center hover:text-white transition-colors group"
                      >
                        <span className="text-amber-400 mr-2 font-bold text-sm leading-none">•</span>
                        <span className="group-hover:underline text-amber-200/90 font-medium">
                          {isFr ? `Vérifier Titre Foncier à ${city.name}` : `Verify Title Deed in ${city.name}`}
                        </span>
                      </Link>
                    </li>

                    <li className="pt-1">
                      <Link
                        to={cityGuideUrl}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-400 hover:text-amber-300 hover:underline transition-colors"
                      >
                        <span>
                          {isFr ? `Guide complet et quartiers de ${city.name} →` : `Full market guide & quarters in ${city.name} →`}
                        </span>
                      </Link>
                    </li>
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    )}
  </div>
</section>
);
};
